import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { filter, map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly appName = 'DanceHub';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Инициализирует SEO-сервис для автоматического обновления метаданных при навигации
   */
  initializeSeoService(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data)
    ).subscribe(data => {
      // Обновление title
      if (data['title']) {
        this.updateTitle(data['title']);
      }
      
      // Обновление метатегов
      if (data['seo'] && data['seo'].metaTags) {
        // Обработка HTML в контенте метатегов
        const cleanedMetaTags = data['seo'].metaTags.map((tag: any) => {
          if (tag.content && typeof tag.content === 'string') {
            return { ...tag, content: this.stripHtml(tag.content) };
          }
          return tag;
        });
        
        this.updateMetaTags(cleanedMetaTags);
      }
      
      // Обновление канонического URL
      this.updateCanonicalUrl();
    });
  }

  /**
   * Обновляет заголовок страницы
   */
  updateTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  /**
   * Обновляет мета-теги из массива
   */
  updateMetaTags(metaTags: any[]): void {
    // Сначала удаляем динамические мета-теги
    this.removeMetaTags();
    
    // Затем добавляем новые
    metaTags.forEach(tag => {
      // Для стандартных мета-тегов
      if (tag.name) {
        this.metaService.addTag({ name: tag.name, content: tag.content });
      } 
      // Для Open Graph и других тегов с property
      else if (tag.property) {
        this.metaService.addTag({ property: tag.property, content: tag.content });
      }
    });
  }

  /**
   * Удаляет динамические мета-теги перед обновлением
   */
  private removeMetaTags(): void {
    const metaTagsToRemove = [
      'description', 'keywords', 'robots', 
      'og:title', 'og:description', 'og:url', 'og:image',
      'twitter:title', 'twitter:description', 'twitter:card'
    ];
    
    metaTagsToRemove.forEach(tagName => {
      this.metaService.removeTag(tagName.startsWith('og:') || tagName.startsWith('twitter:') 
        ? `property='${tagName}'` 
        : `name='${tagName}'`);
    });
  }

  /**
   * Очищает HTML-теги из строки
   */
  stripHtml(html: string): string {
    if (!html) return '';
    
    // Создаем временный div-элемент
    const tempDiv = this.document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Получаем только текстовое содержимое без HTML-тегов
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Очищаем пробельные символы и лишние переносы строк
    return textContent.trim()
      .replace(/\s+/g, ' ') // Заменяем множественные пробелы на один пробел
      .replace(/\n+/g, ' '); // Заменяем переносы строк на пробелы
  }

  /**
   * Обновляет канонический URL для текущей страницы
   */
  updateCanonicalUrl(url?: string): void {
    const canURL = url || this.document.location.href;
    
    // Найти существующий канонический тег или создать новый
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canURL);
  }

  /**
   * Создает JSON-LD разметку для улучшения структурированных данных
   */
  addJsonLd(schema: any): void {
    // Удалить существующий JSON-LD если он есть
    let existingScript = this.document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  /**
   * Устанавливает метаданные для динамического контента
   * (например, для страниц, где данные загружаются асинхронно)
   */
  setDynamicMetadata(title: string, description: string, image?: string, keywords?: string): void {
    // Установка заголовка
    const fullTitle = `${title} | ${this.appName}`;
    this.updateTitle(fullTitle);
    
    // Очистка описания от HTML-тегов
    const cleanDescription = this.stripHtml(description);
    console.log(cleanDescription);
    
    // Создание массива метатегов
    const metaTags = [
      { name: 'description', content: cleanDescription },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: cleanDescription },
      { name: 'robots', content: 'noindex, follow' } // Для приватных страниц
    ];
    
    // Добавление ключевых слов если они предоставлены
    if (keywords) {
      metaTags.push({ name: 'keywords', content: keywords });
    }
    
    // Добавление изображения если оно предоставлено
    if (image) {
      metaTags.push({ property: 'og:image', content: image });
    }
    
    this.updateMetaTags(metaTags);
    this.updateCanonicalUrl();
  }
}